import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { GoMaterialModule } from '@go-modules/go-material.module';
import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { catchError, combineLatest, distinctUntilChanged, EMPTY, filter, map, Subject, take, takeUntil } from 'rxjs';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import dayjs from 'dayjs';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { GoTourService, goTourServiceToken } from 'go-modules/go-tour/go-tour.service';
import { ProductTier } from '@go-modules/interfaces/licenses/product-tier';
import { TranscriptCacheService } from '@go-modules/services/transcript-cache/transcript-cache.service';
import { ExpirationPolicies } from '@go-modules/enums/salesforce-license';
import { LlmFeatures, LlmRelations, LlmTaskStatus } from '@go-modules/interfaces/llm-task';
import { LlmTaskService } from '@go-modules/services/llm-task/llm-task.service';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { CommentBannersService, BannerType } from '@go-modules/services/comment-banners/comment-banners.service';

export const FEEDBACK_DISPLAY_AI_MARKERS_TOUR = 'feedback-display-ai-markers-tour';
export const FEEDBACK_DISPLAY_AI_MARKERS_TOUR_CONSTRAINTS = {
	isOnboarding: false,
	tourStartDate: dayjs('2024-07-01 09:00:00').toDate()
};

export const FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR = 'feedback-display-manage-ai-markers-tour';
export const FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR_CONSTRAINTS = {
	isOnboarding: false,
	tourStartDate: dayjs('2025-01-31 09:00:00').toDate()
};

@Component({
	selector: 'comment-panel-banners',
	templateUrl: './comment-panel-banners.component.html',
	styleUrls: ['./comment-panel-banners.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		GoMaterialModule
	]
})
export class CommentPanelBannersComponent implements OnInit, OnDestroy {
	@Output() public addAiAssistant = new EventEmitter<void>();

	private eventNames: string[];
	private componentDestroyed$$ = new Subject<void>();
	public readonly hasComments$ = this.ngxCommentListService.comments$.pipe(
		map((comments) => comments.length > 0),
		distinctUntilChanged()
	);

	constructor (
		public commentBannersService: CommentBannersService,
		private eventService: EventService,
		private featureFlag: NgxFeatureFlagService,
		private environmentVarsService: EnvironmentVarsService,
		private transcriptCacheService: TranscriptCacheService,
		private llmTaskService: LlmTaskService,
		private ngxCommentListService: CommentListService,
		@Inject(goTourServiceToken) private goTour: GoTourService,
		@Inject(selectedServiceToken) private selectedService: SelectedService
	) {}

	public ngOnInit () {
		this.eventNames = [
			EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED,
			EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_COMPLETED,
			EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_FAILED
		];
		this.eventService.events
			.pipe(
				filter((ev: GoEvent) => this.eventNames.includes(ev.name)),
				takeUntil(this.componentDestroyed$$)
			)
			.subscribe((ev: GoEvent) => {
				switch (ev.name) {
					case EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED:
						this.checkToShowManageAiMarkersTour();
						this.showFeedbackDisplayAiMarkersTour();
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_COMPLETED:
						this.commentBannersService.closeBanner(BannerType.GENERATING_AI_FEEDBACK);
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_FAILED:
						this.commentBannersService.closeBanner(BannerType.GENERATING_AI_FEEDBACK);
						break;
				}
			});

		const session = this.selectedService.getSession();
		this.llmTaskService.getStatus(
			LlmRelations.SESSION,
			session.session_id,
			LlmFeatures.AI_COMMENTS
		)
			.pipe(
				takeUntil(this.componentDestroyed$$)
			)
			.subscribe((response) => {
				if (response.status === LlmTaskStatus.QUEUED || response.status === LlmTaskStatus.PROCESSING) {
					this.commentBannersService.showGeneratingAiFeedbackBanner();
				} else {
					this.commentBannersService.closeBanner(BannerType.GENERATING_AI_FEEDBACK);
				}
			});

		this.checkReadOnlyBanner();
	}

	public ngOnDestroy () {
		this.componentDestroyed$$.next();
		this.componentDestroyed$$.complete();
	}

	public openAiMarkers (event?: MouseEvent) {
		event?.stopPropagation();
		this.addAiAssistant.emit();
	}

	public checkToShowManageAiMarkersTour () {
		const activity = this.selectedService.getActivity();
		const session = this.selectedService.getSession();

		if(!session.media || session.media.isYoutube() || !activity.has_response_media) return;

		const license = this.selectedService.getLicense();

		if (
			this.featureFlag.isAvailable('AI_DEFAULT') &&
			this.selectedService.getGroup().hasInstructorRole(true) &&
			license?.salesforce_license.tier_name === ProductTier.Plus &&
			activity.ai_prompts_enabled &&
			activity.llm_definitions.length > 0 &&
			!(activity.is_parent_sync_enabled && activity.parent_child_sync) &&
			!this.environmentVarsService.get(EnvironmentVarsService.VAR.DISABLE_ACTIVITY_EDITING)
		) {
			this.goTour.canTourBeViewed(
				FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR,
				FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR_CONSTRAINTS
			).then(()=> {
				this.commentBannersService.showManageAiMarkersTour(() => this.openAiMarkers());
			}).catch(() => {});
		}
	}

	public showFeedbackDisplayAiMarkersTour () {
		const activity = this.selectedService.getActivity();
		const session = this.selectedService.getSession();

		if(!session.media || session.media.isYoutube() || !activity.has_response_media) return;

		const transcription$ = this.transcriptCacheService.getTranscription({
			media: session.media,
			includeAnalytics: true
		}).pipe(
			catchError(() => EMPTY)
		);

		combineLatest([
			transcription$,
			this.hasComments$
		]).pipe(
			takeUntil(this.componentDestroyed$$),
			take(1)
		).subscribe(([_transcription, hasComments]) => {
			const license = this.selectedService.getLicense();
			if (
				this.selectedService.getGroup().hasInstructorRole(true) &&
				activity.ai_prompts_enabled &&
				!activity.llm_definitions.length &&
				hasComments &&
				this.featureFlag.isAvailable('LICENSE_UPGRADE_PURCHASE') &&
				license?.salesforce_license.tier_name === ProductTier.Plus
			) {
				this.goTour.canTourBeViewed(
					FEEDBACK_DISPLAY_AI_MARKERS_TOUR,
					FEEDBACK_DISPLAY_AI_MARKERS_TOUR_CONSTRAINTS
				).then(()=> {
					this.commentBannersService.showAiMarkersTour(() => this.openAiMarkers());
				}).catch(() => {});
			}
		});

	}

	public checkReadOnlyBanner () {
		const license = this.selectedService.getLicense();
		const group = this.selectedService.getGroup();

		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			this.commentBannersService.showReadOnlyBanner('session-read-only-text_lti-read-only');
		} else if (!this.hasCourseStarted(group)) {
			this.commentBannersService.showReadOnlyBanner('session-read-only-text_course-not-started');
		} else if (this.hasCourseEnded(group)) {
			this.commentBannersService.showReadOnlyBanner('session-read-only-text_course-ended');
		} else if (
			license &&
			(
				!this.isLicenseActive(license) ||
				(
					license.salesforce_license.expiration_policy === ExpirationPolicies.RESTRICTED &&
					(!this.hasLicenseStarted(license) || this.isLicenseExpired(license))
				)
			)
		) {
			this.commentBannersService.showReadOnlyBanner('session-read-only-text_invalid-license');
		}
	}

	public closeBanner (bannerId: BannerType): void {
		if (bannerId === BannerType.MANAGE_AI_MARKERS) {
			this.goTour.markTourViewed(FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR);
		} else if (bannerId === BannerType.AI_MARKERS_TOUR) {
			this.goTour.markTourViewed(FEEDBACK_DISPLAY_AI_MARKERS_TOUR);
		}
		this.commentBannersService.closeBanner();
	}

	private hasCourseStarted (group): boolean {
		const currentDateUTC = dayjs().utc();
		return !currentDateUTC.isBefore(dayjs(group.start_date));
	}

	private hasCourseEnded (group): boolean {
		const currentDateUTC = dayjs().utc();
		return group.end_date && currentDateUTC.isAfter(dayjs(group.end_date));
	}

	private isLicenseActive (license): boolean {
		return license.is_active;
	}

	private hasLicenseStarted (license): boolean {
		const currentDateUTC = dayjs().utc();
		return !currentDateUTC.isBefore(dayjs.utc(license.current_access_date));
	}

	private isLicenseExpired (license): boolean {
		const currentDateUTC = dayjs().utc();
		return currentDateUTC.isAfter(dayjs.utc(license.ends_at));
	}
}
